import React, { useEffect, useState } from "react";
import "./MyProduct.scss";
import ProductList from "@containers/ProductList/ProductList";
import { ProductApi } from "services/ProductApi";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "contexts/UserContext";
import { Button, Checkbox, Col, Empty, Row, Tabs, TabsProps } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MyProductProperties from "@components/MyProductProperties/MyProductProperties";
import ProductFilter from "@containers/ProductFilter/ProductFilter";
import { IProductSearch } from "@utils/Interfaces";

const MyProduct: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const [activeTab, setActiveTab] = useState("1");
  const [myProducts, setMyProducts] = useState<any>(null);
  const [selectedProductId, setSelectedProductId] = useState<null | number>(
    null
  );

  // FETCH MY PRODUCTS
  useEffect(() => {
    const fetchMyProduct = async () => {
      try {
        const response = await ProductApi.GetMyProducts();
        if (response.status === 200) {
          setMyProducts(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("ProductCatalog.tsx - fetchMyProduct", error);
      }
    };

    if (isAuthenticated && !myProducts && userProfile?.type_ids.includes(2)) {
      fetchMyProduct();
    }
  }, [isAuthenticated, myProducts]);

  const myProductItems: TabsProps["items"] = [
    {
      key: "1",
      label: (
        <div className="tab-label">
          All <span className="tab-amount">{myProducts?.length}</span>
        </div>
      ),
      children: (
        <MyProductList
          selectedProductId={selectedProductId}
          setSelectedProductId={setSelectedProductId}
          myProducts={myProducts}
          setMyProducts={setMyProducts}
          activeTab={activeTab}
        />
      ),
    },
    {
      key: "2",
      label: (
        <div className="tab-label">
          Posted/Selling{" "}
          <span className="tab-amount">
            {myProducts?.filter((p: any) => p.quantity > 0)?.length}
          </span>
        </div>
      ),
      children: (
        <MyProductList
          selectedProductId={selectedProductId}
          setSelectedProductId={setSelectedProductId}
          myProducts={myProducts}
          setMyProducts={setMyProducts}
          activeTab={activeTab}
        />
      ),
    },
    {
      key: "3",
      label: (
        <div className="tab-label">
          Drafts <span className="tab-amount">0</span>
        </div>
      ),
      children: (
        <MyProductList
          selectedProductId={selectedProductId}
          setSelectedProductId={setSelectedProductId}
          myProducts={myProducts}
          setMyProducts={setMyProducts}
          activeTab={activeTab}
        />
      ),
    },
    {
      key: "4",
      label: (
        <div className="tab-label">
          Sold/Ended{" "}
          <span className="tab-amount">
            {myProducts?.filter((p: any) => p.quantity === 0)?.length}
          </span>
        </div>
      ),
      children: (
        <MyProductList
          selectedProductId={selectedProductId}
          setSelectedProductId={setSelectedProductId}
          myProducts={myProducts}
          setMyProducts={setMyProducts}
          activeTab={activeTab}
        />
      ),
    },
  ];

  return (
    <div className="MyProduct mt-3">
      <Tabs
        className="product-tabs"
        activeKey={activeTab}
        items={myProductItems}
        onChange={(key) => setActiveTab(key)}
      />
    </div>
  );
};

const MyProductList: React.FC<any> = ({
  selectedProductId,
  setSelectedProductId,
  myProducts,
  setMyProducts,
  activeTab,
}) => {
  const navigate = useNavigate();
  const [filteredProduct, setFilteredProduct] = useState<any>(
    myProducts || null
  );
  const [isOpenMap, setIsOpenMap] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<number[]>([]);
  const [isLargeMode, setIsLargeMode] = useState(false);
  const { userProfile } = useUser();
  const [searchParam, setSearchParam] = useState<IProductSearch>({
    query: "",
    type: null,
    location: null,
    selling_method: null,
    categories_id: userProfile?.farm_info.category_ids || [1, 2],
    quantity: null,
    radius: 300,
    address: "",
    filters: {
      hay_cut: [],
      bale_weight_min: 0,
      bale_weight_max: 200,
      shape: [],
      size: [],
      hay_compression: [],
      moisture: [],
      rain: [],
      storage: [],
      crop: [],
      binding_type: [],
      price_min: 0,
      price_max: 1000,
      shipping_options: [],
      payment_options: [],
      crude_protein_range_ids: [],
    },
  });

  useEffect(() => {
    switch (activeTab) {
      case "2":
        setFilteredProduct(myProducts.filter((p: any) => p.quantity > 0));
        break;
      case "3":
        setFilteredProduct([]);
        break;
      case "4":
        setFilteredProduct(myProducts.filter((p: any) => p.quantity === 0));
        break;
      default:
        setFilteredProduct(myProducts);
        break;
    }
  }, [activeTab, myProducts]);

  const handleSearch = async () => {
    setFilteredProduct(null);
    try {
      const response = await ProductApi.Search(searchParam);
      if (response.status === 200) {
        setFilteredProduct(response.data || []);
      }
    } catch (error) {
      Helpers.notification.error("Can not search product. Please try again");
      errorLog("MyProduct.tsx - handleSearch", error);
    }
  };

  const handleDeleteProducts = async () => {
    try {
      const response = await ProductApi.DeleteProduct({
        product_ids: selectedProduct,
      });
      if (response.status === 200) {
        const newList = myProducts.filter(
          (p: any) => !response.data.data.successful.includes(p.product_id)
        );
        setMyProducts(newList);
        setFilteredProduct(newList);
      }
    } catch (error) {
      Helpers.notification.error("Can not delete product. Please try again");
      errorLog("MyProduct.tsx - handleDeleteProducts", error);
    }
  };

  return (
    <>
      <ProductFilter
        isOpenMap={isOpenMap}
        setIsOpenMap={setIsOpenMap}
        isLargeMode={isLargeMode}
        setIsLargeMode={setIsLargeMode}
        searchParam={searchParam}
        setSearchParam={setSearchParam}
        componentIn="myProduct"
        submit={handleSearch}
      />
      <div className="my-product-list-control">
        <Checkbox
          checked={
            selectedProduct.length !== 0 &&
            selectedProduct?.length === filteredProduct?.length
          }
          onClick={(e: any) => {
            if (e.target.checked) {
              setSelectedProduct(filteredProduct.map((p: any) => p.product_id));
            } else {
              setSelectedProduct([]);
            }
          }}
        />
        <Button
          className="delete-products-btn"
          icon={<DeleteOutlined />}
          onClick={() => {
            Helpers.confirmModal(
              "These products will be deleted and you can not restore. Are you sure?",
              handleDeleteProducts
            );
          }}
          disabled={selectedProduct.length === 0}
        />
        <Button
          className="register-btn"
          icon={<PlusOutlined />}
          onClick={() => {
            navigate(
              window.location.pathname.includes("all")
                ? window.location.pathname.replace("all", "register")
                : "/product/register"
            );
          }}
        >
          Product
        </Button>
      </div>
      <Row gutter={[16, 16]}>
        <Col span={selectedProductId ? 6 : 24}>
          <ProductList
            type="My Products"
            setSelectedProductId={setSelectedProductId}
            selectedProductId={selectedProductId}
            products={filteredProduct}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
          />
        </Col>
        <Col span={selectedProductId ? 18 : 0}>
          <MyProductProperties
            selectedProductId={selectedProductId}
            setSelectedProductId={setSelectedProductId}
            setMyProducts={setMyProducts}
            data={
              myProducts &&
              myProducts.find((p: any) => p.product_id === selectedProductId)
            }
          />
        </Col>
      </Row>
    </>
  );
};
export default MyProduct;
